import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useSession } from '../auth';
import { trackFailed } from '../../../../shared-web/track';
import { noteVisitorConsent, useVisitorConsent } from '../../../../shared-web/inbound-door';
import { emberApi } from './api';
import { navigate, sanitizeReturnTo } from './router';
import { Link } from './components/Link';
import { BottomSheet } from './components/BottomSheet';
import { CallGlyph } from './components/CallGlyph';
import { ConsentTerms, CONSENT_AGE } from './components/ConsentTerms';
import './talk-sheet.css';

export interface TalkSheetRequest {
  /** Entry attribution, carried into whichever mode is picked (entry_type). */
  entryType?: string;
  /** The Request / Theme / Topic this door was opened from (entry_id). */
  entryId?: string;
  /** Where Back lands from the conversation. Defaults to the current URL. */
  returnTo?: string;
}

/**
 * The door has one name wherever it stands (2026-09-08 走查): the sheet from
 * the homepage, the sheet from a light, the /talk page a link lands on. Four
 * titles for one door read as four different doors.
 */
export const TELLING_DOOR_TITLE = 'Tell it your way';

/** The door's title — the same for every entrance. */
export function entryTitle(_entryType?: string | null): string {
  return TELLING_DOOR_TITLE;
}

/**
 * The line under every telling door: what happens when you start, and —
 * smaller — the data contract, said at the moment of choosing, every time
 * (2026-09-12 拍板: this is the disclosure that matters, so it is here and not
 * only in the Privacy Policy; no checkbox, no Accept — continuing is the
 * answer). The compact form of the site's one vocabulary for this (content.js
 * header): private by default; the person's experience may help Ember understand
 * similar situations, off anytime; no one reads them unless they separately
 * say yes (Ember asks the owner once, before anyone is offered it). Before the first sentence
 * the less to take in the better, so "identifying details removed" is left to
 * Privacy →.
 */
export const TELLING_PRIVACY_LINE = 'Private by default. Your experience may help Ember understand similar situations; you can turn this off anytime. No one can read your Embers unless you separately say yes.';

export function entryDisclosure(_entryType?: string | null): ReactNode {
  const lead = 'Start anywhere. Ember listens and asks when it helps.';
  return (
    <>
      {lead}
      <span className="talk-privacy-line">
        {TELLING_PRIVACY_LINE}
        {' '}
        <a href="/privacy-and-sharing" className="talk-privacy-link" data-log="talk_privacy_open">Privacy →</a>
      </span>
    </>
  );
}

const TalkSheetContext = createContext<(request?: TalkSheetRequest) => void>(() => {});
/** Whether the telling doors are open over the page (2026-10-26): no banner drops over them. */
export const TalkSheetOpenContext = createContext(false);

/**
 * Open the telling doors over the page the person is already on.
 *
 * Every in-app entrance uses this instead of navigating to /talk (2026-08-21):
 * the choice between typing and speaking is one short question, and answering
 * it never justified losing the page underneath. /talk itself stays as the
 * page for links that arrive from outside the app — Ember's own texts, a
 * /topic/:slug alias, a bookmark — and `mode=chat` is still the chat surface.
 */
export function useTalkSheet(): (request?: TalkSheetRequest) => void {
  return useContext(TalkSheetContext);
}

export function TalkSheetProvider({ children }: { children: ReactNode }): ReactElement {
  const [request, setRequest] = useState<TalkSheetRequest | null>(null);
  const open = useCallback((next: TalkSheetRequest = {}) => setRequest(next), []);
  return (
    <TalkSheetContext.Provider value={open}>
      <TalkSheetOpenContext.Provider value={request !== null}>
        {children}
        {request ? (
          <TalkSheet request={request} onClose={() => setRequest(null)} />
        ) : null}
      </TalkSheetOpenContext.Provider>
    </TalkSheetContext.Provider>
  );
}

/**
 * The two telling doors, and nothing else: type it here, or say it out loud.
 *
 * The messaging door the /talk page used to carry as a third card is not here
 * — this sheet is the entrance to a telling, and a handoff to the account's
 * own Ember line is a different errand. Nothing about that line changed.
 *
 * Neither door is dressed as the one this sheet prefers: both wear the solid
 * accent fill. Typing and speaking are the same telling by two routes, and a
 * quiet second button was reading as the lesser choice rather than the other
 * half of one question.
 */
/**
 * Where the two telling doors lead, for whichever surface draws them — the
 * TalkSheet, or the landing hero's in-place split (v3.0 §4.1, 2026-08-30).
 *
 * Both doors spend ONE free-telling budget while signed out
 * (GUEST_FREE_TELLINGS): when it is spent, each door leads through the /talk
 * chooser, which renders the inbound-SMS registration state (v3.0 §4.5) —
 * never /auth signup. The servers enforce the budget either way, so while the
 * read is still in flight the doors stay pointed at the telling — a guest who
 * still has theirs must never be sent to a sign-in page because a
 * telemetry-grade read was slow, and one whose budget is spent loses only one
 * hop.
 */
export function useTellingDoors(request: TalkSheetRequest): {
  /** Budget spent while signed out: both doors route to registration. */
  spent: boolean;
  /** In-app route for the message door (the registration chooser once spent). */
  chatTo: string;
  /** Full-page href for the browser call (server-rendered telling page). */
  callHref: string;
  /** In-app route for the call door once the budget is spent. */
  registerTo: string;
  /** Entry context, in attribute form, for the surface's exposure record. */
  entryLogAttrs: Record<string, string>;
  /** entryLogAttrs plus the gated state, for each door. */
  doorLogAttrs: Record<string, string>;
} {
  const { status } = useSession();
  const anonymous = status === 'anonymous';
  const [gated, setGated] = useState<boolean | null>(null);

  useEffect(() => {
    if (!anonymous) return undefined;
    let alive = true;
    void emberApi.tellingQuota()
      .then((quota) => { if (alive) setGated(quota.requires_registration === true || quota.requires_auth === true); })
      .catch(() => {
        if (!alive) return;
        trackFailed('talk_gate', 'quota_unavailable');
        setGated(false);
      });
    return () => { alive = false; };
  }, [anonymous]);

  const spent = anonymous && gated === true;

  const here = typeof window === 'undefined'
    ? '/'
    : `${window.location.pathname}${window.location.search}`;
  const returnTo = sanitizeReturnTo(request.returnTo || here, '');

  const entry = new URLSearchParams();
  if (request.entryType) entry.set('entry_type', request.entryType);
  if (request.entryId) entry.set('entry_id', request.entryId);

  const chatParams = new URLSearchParams(entry);
  chatParams.set('mode', 'chat');
  if (returnTo) chatParams.set('returnTo', returnTo);

  // Signed out, the browser call is the guest telling; signed in, it dials the
  // account's own telling page. Full page load either way: the call surface is
  // the server-rendered telling page, not an SPA route.
  const callParams = new URLSearchParams(entry);
  if (returnTo) callParams.set('returnTo', returnTo);
  const callQs = callParams.toString();
  const callHref = `/collect?${anonymous ? 'trial=1' : 'call=1'}${callQs ? `&${callQs}` : ''}`;
  const registerTo = `/talk${entry.toString() ? `?${entry.toString()}` : ''}`;
  const chatTo = spent ? registerTo : `/talk?${chatParams.toString()}`;

  const entryLogAttrs: Record<string, string> = {
    'data-log-discovery': request.entryType || 'home',
    ...(request.entryType === 'request' && request.entryId
      ? { 'data-log-request-id': request.entryId }
      : {}),
    ...(request.entryType === 'node' && request.entryId
      ? { 'data-log-node-id': request.entryId }
      : {}),
  };
  const doorLogAttrs = { ...entryLogAttrs, ...(spent ? { 'data-log-state': 'gated' } : {}) };

  return { spent, chatTo, callHref, registerTo, entryLogAttrs, doorLogAttrs };
}

/**
 * The telling door itself — the one module every entrance calls (2026-09-08
 * 拍板): the homepage's Start here, a light's I've been here too, a Place
 * page's door, the notebook's Talk to Ember, the footer, and the /talk page a
 * link from outside lands on. Two ways in, Write it / Talk it out, and, the
 * first time a signed-out browser is here, the two acknowledgments (18+, the
 * Terms) on the door itself rather than on a page of their own after it: the
 * boxes are what opens the door, so they sit on the door. Ticked once, the
 * browser is not asked again (the visitor consent record, §4.5); the server
 * still refuses a telling without it (`consent_required`), so a client that
 * skips the boxes gains nothing. A signed-in person accepted at /auth and is
 * never asked here.
 *
 * Until both boxes are ticked the doors are drawn but do not open. Taking a
 * door with the boxes ticked records the acknowledgment first (POST
 * /api/visitor/consent) and then goes — so the chat opens without asking, and
 * the call page, reading the same record, draws no boxes of its own.
 */
export function TellingDoors({ request, onChosen, className = '', discovery }: {
  request: TalkSheetRequest;
  /** A door was taken (the sheet closes itself on it). */
  onChosen?: () => void;
  className?: string;
  /** Where these doors stand, when it is not the sheet (`tree_ember`: Ember's card under the tree). */
  discovery?: string;
}): ReactElement {
  const { spent, chatTo, callHref, registerTo, doorLogAttrs: baseLogAttrs } = useTellingDoors(request);
  const doorLogAttrs = discovery ? { ...baseLogAttrs, 'data-log-discovery': discovery } : baseLogAttrs;
  const { status } = useSession();
  const consent = useVisitorConsent();
  const [ageOk, setAgeOk] = useState(false);
  const [tosOk, setTosOk] = useState(false);
  const asking = status === 'anonymous' && consent.known && !consent.consented && !spent;
  const waiting = asking && !(ageOk && tosOk);
  // The record is still being read (a signed-out browser's first seconds on a slow link): a door taken now is kept, and
  // taken for them the moment the answer comes — the chat it opened onto refused the telling (consent_required) and
  // sent the person back here, where the boxes had meanwhile appeared (dev e2e 2026-10-19). With the boxes due, it
  // stays put: they are now on the door.
  const settling = status === 'anonymous' && !consent.known && !spent;
  const kept = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (settling || !kept.current) return;
    const go = kept.current;
    kept.current = null;
    if (asking) return;
    onChosen?.();
    go();
  }, [settling, asking, onChosen]);
  const doorClass = `ember-btn ember-btn-primary talk-sheet-btn${waiting ? ' is-waiting' : ''}`;
  // A door taken: held shut until the boxes are ticked; with them ticked the
  // record is written before the page moves, so what opens next never asks.
  // A failed write still goes — the server refuses on its own if the record
  // is truly missing, and the door asks again then.
  const take = (go: () => void) => (e: { preventDefault: () => void }): void => {
    if (waiting) { e.preventDefault(); return; }
    if (settling) { e.preventDefault(); kept.current = go; return; }
    if (!asking) { onChosen?.(); return; }
    e.preventDefault();
    void emberApi.visitorConsent()
      .then(() => noteVisitorConsent(new Date().toISOString()))
      .catch(() => trackFailed('talk_consent', 'record_failed'))
      .finally(() => { onChosen?.(); go(); });
  };

  return (
    <div className={`talk-doors${className ? ` ${className}` : ''}`}>
      {asking ? (
        <div className="talk-doors-consent" data-log-seen="talk_consent_gate" {...doorLogAttrs}>
          <label className="talk-doors-check">
            <input type="checkbox" checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} data-log="talk_consent_age" />
            <span>{CONSENT_AGE}</span>
          </label>
          <label className="talk-doors-check">
            <input type="checkbox" checked={tosOk} onChange={(e) => setTosOk(e.target.checked)} data-log="talk_consent_terms" />
            <ConsentTerms door="talk" />
          </label>
        </div>
      ) : null}
      <Link
        to={chatTo}
        className={doorClass}
        data-log="talk_choose_message"
        {...doorLogAttrs}
        aria-disabled={waiting || undefined}
        onClick={take(() => navigate(chatTo))}
      >
        <ChatGlyph />
        <span>Write it</span>
      </Link>
      {spent ? (
        <Link
          to={registerTo}
          className={doorClass}
          data-log="talk_choose_call"
          {...doorLogAttrs}
          onClick={take(() => navigate(registerTo))}
        >
          <CallGlyph className="talk-sheet-icon" />
          <span>Talk it out</span>
        </Link>
      ) : (
        <a
          href={callHref}
          className={doorClass}
          data-log="talk_choose_call"
          {...doorLogAttrs}
          aria-disabled={waiting || undefined}
          onClick={take(() => window.location.assign(callHref))}
        >
          <CallGlyph className="talk-sheet-icon" />
          <span>Talk it out</span>
        </a>
      )}
    </div>
  );
}

function TalkSheet({ request, onClose }: {
  request: TalkSheetRequest;
  onClose: () => void;
}): ReactElement {
  const { doorLogAttrs } = useTellingDoors(request);

  return (
    <BottomSheet
      onClose={onClose}
      // One title for the door wherever it opens; the one line under it is the
      // door's consequence, read once, at the moment of choosing. From a
      // Request deep link that line is the conditional pre-authorization every
      // Request entry carries (§9.12A). Closing the sheet is backing out, so it
      // still precedes any telling.
      title={entryTitle(request.entryType)}
      description={entryDisclosure(request.entryType)}
      logSeen="talk_sheet_open"
      // doorLogAttrs, not entryLogAttrs: a gated sheet (state=gated) must be
      // separable from an open one in the exposure too, or the saw→clicked
      // funnel joins a gated door's clicks onto an ungated denominator.
      logSeenAttrs={doorLogAttrs}
      className="talk-sheet"
    >
      <TellingDoors request={request} onChosen={onClose} />
    </BottomSheet>
  );
}

export function ChatGlyph(): ReactElement {
  return (
    <svg className="talk-sheet-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 12a8 8 0 0 1-8 8H5l-2 2V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8Z" />
    </svg>
  );
}
